import React, { useState, useEffect, useMemo } from 'react';
import { 
  Lock, Eye, Download, Search, FileDown, LogOut, 
  ChevronUp, ChevronDown, Plane, User, Phone, Mail, 
  Calendar, AlertCircle, Key
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = most recent first

  // Check URL param for auto-login or use stored password
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlKey = urlParams.get('key');

    if (urlKey) {
      // Auto-login via URL (bypasses keyboard/autofill issues)
      const cleanKey = urlKey.trim();
      setPassword(cleanKey);
      axios.post(`${API}/admin/login`, { password: cleanKey })
        .then(() => {
          sessionStorage.setItem('adminPassword', cleanKey);
          setIsAuthenticated(true);
          toast.success('Connexion réussie via lien direct');
          // Clean URL to remove password from history
          window.history.replaceState({}, '', '/admin');
        })
        .catch((err) => {
          const status = err.response?.status;
          toast.error(`Lien invalide (${status || 'erreur réseau'})`);
          setPassword('');
        });
      return;
    }

    const storedPassword = sessionStorage.getItem('adminPassword');
    if (storedPassword) {
      setPassword(storedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch RSVPs when authenticated
  useEffect(() => {
    if (isAuthenticated && password) {
      fetchRSVPs();
    }
  }, [isAuthenticated, password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem('adminPassword', password);
      setIsAuthenticated(true);
      toast.success('Connexion réussie');
    } catch (error) {
      toast.error('Mot de passe incorrect');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminPassword');
    setIsAuthenticated(false);
    setPassword('');
    setRsvps([]);
    toast.success('Déconnexion réussie');
  };

  const fetchRSVPs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/rsvps`, {
        headers: { 'X-Admin-Password': password }
      });
      setRsvps(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (rsvpId) => {
    window.open(`${API}/admin/ticket/${rsvpId}?password=${encodeURIComponent(password)}`, '_blank');
  };

  const handleDownloadTicket = async (rsvpId, filename) => {
    try {
      // Use query parameter for download as well
      const response = await axios.get(`${API}/admin/ticket/${rsvpId}/download?password=${encodeURIComponent(password)}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'ticket');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Téléchargement démarré');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-csv`, {
        headers: { 'X-Admin-Password': password },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rsvp_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Filter and sort RSVPs
  const filteredRSVPs = useMemo(() => {
    let filtered = rsvps.filter(rsvp => {
      const searchLower = searchTerm.toLowerCase();
      return (
        rsvp.nom?.toLowerCase().includes(searchLower) ||
        rsvp.prenom?.toLowerCase().includes(searchLower) ||
        rsvp.telephone?.includes(searchTerm)
      );
    });

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [rsvps, searchTerm, sortOrder]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-md">
          <div className="bg-[#fcf6f1] border border-[#e8ddd4] rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#8d5f3f] flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-display text-3xl text-[#8d5f3f]">Administration</h1>
              <p className="text-[#8d5f3f]/70 mt-2">Accès réservé aux organisateurs</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[#8d5f3f] text-sm font-medium">Mot de passe</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez le mot de passe"
                  className="bg-white border-[#e8ddd4] focus:border-[#e17c3b] focus:ring-[#e17c3b]"
                  data-testid="admin-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !password}
                className="w-full rounded-full py-6 bg-[#8d5f3f] hover:bg-[#6d4a32] text-white"
                data-testid="admin-login-button"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="admin-dashboard">
      {/* Header */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-display text-4xl text-[#e17c3b]">Administration RSVP</h1>
              <p className="text-[#8d5f3f]/70 mt-1">{rsvps.length} réponse(s) enregistrée(s)</p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={handleExportCSV}
                variant="outline"
                className="rounded-full border-[#e8ddd4] text-[#8d5f3f] hover:bg-[#fcf6f1]"
                data-testid="export-csv-button"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Exporter CSV
              </Button>
              
              <Button
                onClick={handleLogout}
                variant="outline"
                className="rounded-full border-[#e17c3b] text-[#e17c3b] hover:bg-[#e17c3b]/10"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>

          {/* Search and Sort Controls */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8d5f3f]/50" />
              <Input
                type="text"
                placeholder="Rechercher par nom ou téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-[#e8ddd4] focus:border-[#e17c3b] focus:ring-[#e17c3b] rounded-full"
                data-testid="search-input"
              />
            </div>
            
            <Button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              variant="outline"
              className="rounded-full border-[#e8ddd4] text-[#8d5f3f] hover:bg-[#fcf6f1]"
              data-testid="sort-button"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Date {sortOrder === 'desc' ? <ChevronDown className="w-4 h-4 ml-1" /> : <ChevronUp className="w-4 h-4 ml-1" />}
            </Button>
          </div>

          {/* RSVP Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#e17c3b] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-[#8d5f3f]/70 mt-4">Chargement...</p>
            </div>
          ) : filteredRSVPs.length === 0 ? (
            <div className="text-center py-12 bg-[#fcf6f1] rounded-2xl border border-[#e8ddd4]">
              <AlertCircle className="w-12 h-12 text-[#8d5f3f]/50 mx-auto mb-4" />
              <p className="text-[#8d5f3f]/70">
                {searchTerm ? 'Aucun résultat trouvé' : 'Aucun RSVP enregistré'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-2xl overflow-hidden shadow-md" data-testid="rsvp-table">
                <thead className="bg-[#8d5f3f] text-white">
                  <tr>
                    <th className="px-4 py-4 text-left font-medium">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Date
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left font-medium">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Nom
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left font-medium">Prénom</th>
                    <th className="px-4 py-4 text-left font-medium">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Téléphone
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left font-medium">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left font-medium">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        Code
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left font-medium">Allergies</th>
                    <th className="px-4 py-4 text-left font-medium">
                      <div className="flex items-center gap-2">
                        <Plane className="w-4 h-4" />
                        Billet
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRSVPs.map((rsvp, index) => (
                    <tr 
                      key={rsvp.id} 
                      className={`border-b border-[#e8ddd4] hover:bg-[#fcf6f1]/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-[#fcf6f1]/30'}`}
                      data-testid={`rsvp-row-${index}`}
                    >
                      <td className="px-4 py-4 text-[#8d5f3f] text-sm whitespace-nowrap">
                        {formatDate(rsvp.created_at)}
                      </td>
                      <td className="px-4 py-4 text-[#8d5f3f] font-medium">{rsvp.nom}</td>
                      <td className="px-4 py-4 text-[#8d5f3f]">{rsvp.prenom}</td>
                      <td className="px-4 py-4 text-[#8d5f3f]">{rsvp.telephone}</td>
                      <td className="px-4 py-4 text-[#8d5f3f] text-sm">
                        {rsvp.email || <span className="text-[#8d5f3f]/40">-</span>}
                      </td>
                      <td className="px-4 py-4 text-[#8d5f3f]">
                        {rsvp.code_rsvp || <span className="text-[#8d5f3f]/40">-</span>}
                      </td>
                      <td className="px-4 py-4 text-[#8d5f3f] text-sm max-w-[150px] truncate">
                        {rsvp.allergies || <span className="text-[#8d5f3f]/40">Aucune</span>}
                      </td>
                      <td className="px-4 py-4">
                        {rsvp.has_ticket || rsvp.ticket_filename ? (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewTicket(rsvp.id)}
                              className="rounded-full text-xs border-[#e17c3b] text-[#e17c3b] hover:bg-[#e17c3b]/10"
                              data-testid={`view-ticket-${index}`}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Voir
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadTicket(rsvp.id, rsvp.ticket_filename)}
                              className="rounded-full text-xs border-[#6b8e4e] text-[#6b8e4e] hover:bg-[#6b8e4e]/10"
                              data-testid={`download-ticket-${index}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Télécharger
                            </Button>
                          </div>
                        ) : (
                          <span className="text-[#8d5f3f]/40 text-sm">Non fourni</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
